import * as Voice from "@twilio/voice-sdk";
import InboundCall from "./inbound-call";
import OutboundCall from "./outbound-call";
import axios from "axios";
import i18n from "../../i18n";
import store from "../../store";
import { validatePhoneForE164, fullName } from "@/common/utils";
import { IS_EMERGENCY, isExtensionCall } from "@/common/const";
import { operateHeadset } from "@/common/headset";
import { getAudioDeviceManager } from "@/composables/useAudioDeviceManager";

let VoiceDevice = null;

let updatingToken = false;

let isInitializing = false;

let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 10;

const allowedAutoplay = () => navigator.userActivation?.hasBeenActive || autoplayEnabled();

// Patch Audio.play() to silently catch NotAllowedError (Chrome autoplay policy)
// This prevents uncaught errors from Twilio SDK internal play() calls on page refresh
const originalPlay = HTMLMediaElement.prototype.play;

HTMLMediaElement.prototype.play = function () {
	return originalPlay.call(this).catch((err) => {
		if (err.name !== "NotAllowedError") throw err;
	});
};

const autoplayEnabled = () => {
	const AudioContext = window.AudioContext || window.webkitAudioContext;
	const testContext = new AudioContext();
	const allowed = (testContext.state === "running");
	
	testContext.close();
	return allowed;
};

const onInboundCall = async (call) => {
	console.debug("[CALL] onInboundCall");

	if (VoiceDevice.isBusy || store.getters["voice/hasIncomingCall"]) {
		console.debug("[CALL] Inbound call rejected. Already on call");
		call?.reject();
		return;
	}

	await operateHeadset(false, false, true, false);

	const inboundCall = InboundCall.from(call);

	if (inboundCall.callDetails.fromQueue) {
		inboundCall.countDuration();
		inboundCall.accept();
		store.commit("voice/setInboundQueueCall", inboundCall);
	} else {
		store.commit("voice/setInboundCall", inboundCall);

		// Triggered when an incoming connection accepted
		inboundCall.callInstance.on("accept", () => {
			operateHeadset(true, false, false, false);
			inboundCall.countDuration();
			inboundCall.answered();
		});
	}

	// Triggered when the user cancelled the call without answering it
	inboundCall.callInstance.on("reject", async () => {
		await operateHeadset(false, false, false, false);
		store.dispatch("voice/endCall");
	});

	// Triggered any time a call is closed
	inboundCall.callInstance.on("disconnect", () => {
		operateHeadset(false, false, false, false);
		store.dispatch("voice/endCall");
	});

	inboundCall.callInstance.on("cancel", async () => {
		await operateHeadset(false, false, false, false);
		store.dispatch("voice/endCall");
	});
};

const outboundCall = async (phoneNumber, patient) => {
	console.debug("[OUTBOUND CALL] outboundCall ", patient);

	if (!VoiceDevice) {
		console.error("[CALL] VoiceDevice not yet initialized");
		return;
	}

	if (VoiceDevice.isBusy || store.getters["voice/hasIncomingCall"]) {
		console.warn("[CALL] outbound call rejected. Already on call");
		return;
	}

	if (
		!IS_EMERGENCY(phoneNumber) &&
		!isExtensionCall(phoneNumber) &&
		!validatePhoneForE164(phoneNumber)
	) {
		store.commit("alerts/add", {
			type: "error",
			message: i18n.t("error.phone-invalid"),
			timeout: false
		}, { root: true });
		return;
	}

	const params = {
		From: store.getters["login/userGroup"]?.phoneNumber,
		To: phoneNumber
	};

	if (patient) {
		params.patientName = fullName(patient);
		params.businessName = patient.business ?? "";
	} else if (IS_EMERGENCY(phoneNumber)) {
		params.patientName = i18n.t("emergency-call");
	}

	if (isExtensionCall(phoneNumber)) {
		// ext.<id> is not a dialable number: display the extension's own number, if any
		params.patientPhone = patient?.phone ?? "";
	}

	const outboundCallInstance = await VoiceDevice.connect( { params });
	const outboundCall = OutboundCall.from(outboundCallInstance, params);

	store.commit("voice/setOutboundCall", outboundCall);

	outboundCall.callInstance.on("accept", async () => {
		console.debug("[CALL] Outbound Call Accepted");
		await operateHeadset(true, false, false, false);
	});

	outboundCall.callInstance.on("reject", () => {
		operateHeadset(false, false, false, false);
		store.dispatch("voice/endCall");
	});

	outboundCall.callInstance.on("disconnect", () => {
		operateHeadset(false, false, false, false);
		store.dispatch("voice/endCall");
	});

	outboundCall.callInstance.on("error", async (err) => {
		console.debug("[CALL] Outbound Call Error");
		await operateHeadset(false, false, false, false);
		if (err?.name === "ConnectionError") {
			store.commit("alerts/add", {
				type: "error",
				message: i18n.t("error.call-error"),
				timeout: true
			}, { root: true });
		}
	});

	outboundCall.start();
};

const pickQueuedCall = async (callSid) => {
	try {
		await axios.post("/voice/v1/queues/pick", {	callSid });
	} catch (err) {
		console.error("[CALL] The call is no longer available:", err);
	}
};

// Answered by a colleague (409) or already over (404): the card is stale
const onAnswerError = (err, callId, answered) => {
	console.error(err);

	if ([404, 409].includes(err?.response?.status)) {
		// A newer event (e.g. a colleague's in-progress) may have replaced the card meanwhile
		if (store.state.calls.calls[callId] === answered) {
			store.commit("calls/releaseCall", callId);
		}

		store.commit("alerts/add", {
			type: "warning",
			message: i18n.t("error.call-unavailable"),
			timeout: true,
			textColor: "black"
		}, { root: true });
	}
};

const answerCall = async (callId) => {
	const answered = store.state.calls.calls[callId];

	store.commit("voice/disconnectCall");
	axios.post("/voice/v1/calls/answer", { callId }).catch(err => onAnswerError(err, callId, answered));
};

const rejectCall = async (callId) => {
	store.commit("voice/disconnectCall");
	axios.post("voice/v1/calls/reject", { callId }).catch(err => console.error(err));
};

// voice device initialization and refresh logic
// ---------------------------------------------

/**
 * Resolves once the user has performed at least one gesture (click, keydown, touchstart).
 * Chrome (and other browsers) require a user gesture before an AudioContext can start.
 * Twilio Voice.Device creates an AudioContext internally, so we must wait.
 */
let _userGestureOccurred = false;
let _gestureResolvers = [];

function _onFirstGesture() {
	if (_userGestureOccurred) return;
	_userGestureOccurred = true;

	// Resolve all pending waiters
	for (const resolve of _gestureResolvers) resolve();
	_gestureResolvers = [];

	// Clean up listeners
	for (const evt of ["click", "keydown", "touchstart", "pointerdown"]) {
		document.removeEventListener(evt, _onFirstGesture, { capture: true });
	}
}

// Install gesture listeners immediately (passive, capture so we catch it early)
for (const evt of ["click", "keydown", "touchstart", "pointerdown"]) {
	document.addEventListener(evt, _onFirstGesture, { capture: true, passive: true, once: false });
}

function waitForUserGesture() {
	if (_userGestureOccurred) return Promise.resolve();
	return new Promise((resolve) => _gestureResolvers.push(resolve));
}

const bootstrap = (commit) => {
	console.debug("[CALL] bootstrap");
	if (commit) {
		commit("disableMicrophone");
	} else {
		store.commit("voice/disableMicrophone");
	}

	// If device exists, just update the token regardless of state
	if (VoiceDevice) {
		updateVoiceToken();
	} else {
		// Defer until a user gesture so the AudioContext can start
		waitForUserGesture().then(() => initVoiceDevice());
	}
};

const getAccessToken = async () => {
	const { data: { jwt }  } = await axios.get("/voice/v1/tokens");

	return jwt;
};

const refreshAccessToken = async () => {
	const { data: { jwt } } = await axios.get("/voice/v1/tokens/refresh");

	return jwt;
};

const updateVoiceToken = async () => {
	console.debug("[CALL] updateVoiceToken");

	if (updatingToken) {
		console.debug("[CALL] Unable to updateVoiceToken: Already in progress");
		return;
	}

	if (!VoiceDevice) {
		setTimeout(initVoiceDevice, 2000);
		return;
	}

	updatingToken = true;
	try {
		const token = await refreshAccessToken();

		const previousState = VoiceDevice.state;

		VoiceDevice.updateToken(token);
		refreshAttempts = 0;

		if (VoiceDevice.state === "unregistered") {
			VoiceDevice.register();
		} else if (VoiceDevice.state !== previousState) {
			console.debug("[CALL] Device state changed after updating token");
		}

		console.debug("[CALL] Token updated successfully");
	} catch (err) {
		console.error(`[CALL] Error updating token (attempt ${refreshAttempts + 1}):`, err);

		if (refreshAttempts < MAX_REFRESH_ATTEMPTS) {
			refreshAttempts++;
			const delay = 1000 * refreshAttempts;

			setTimeout(updateVoiceToken, delay);
		} else {
			refreshAttempts = 0;
			// After MAX_REFRESH_ATTEMPTS attempts, reinit the device
			setTimeout(initVoiceDevice, 3000);
		}
	} finally {
		updatingToken = false;
	}
};

async function initVoiceDevice() {
	if (isInitializing) {
		console.debug("[CALL] VoiceDevice initialization already in progress");
		return;
	}

	isInitializing = true;

	try {
		if (VoiceDevice) {
			cleanup();
		}

		console.debug("[CALL] Initializing VoiceDevice allowedAutoplay :", allowedAutoplay());

		const accessToken = await getAccessToken();

		VoiceDevice = new Voice.Device(accessToken, {
			allowIncomingWhileBusy: false,
			disableAudioContextSounds: !allowedAutoplay()
		});

		// Only disable sounds and wait for gesture if not this is a refresh (no prior user interaction)
		if (!allowedAutoplay()) {
			store.commit("alerts/add", {
				type: "info",
				message: i18n.t("user-interaction-required"),
				timeout: false
			}, { root: true });
		}

		VoiceDevice.on("tokenWillExpire", updateVoiceToken);

		VoiceDevice.on("registered", () => {
			if (VoiceDevice.identity !== store.getters["login/profile"]?.id) {
				console.debug(`[CALL] Registration identity mismatch ${VoiceDevice.identity}, ${store.getters["login/profile"]?.id}. Reinitializing VoiceDevice`);
				setTimeout(initVoiceDevice, 2000);
				return;
			}
			console.debug("[CALL] VoiceDevice registered successfully");
			store.commit("voice/setReady", true);

			// Wire Twilio Device audio to the AudioDeviceManager after registration,
			// so Twilio has enumerated its devices and can accept setInputDevice/speakerDevices.set calls
			const audioDeviceManager = getAudioDeviceManager();

			audioDeviceManager.setTwilioDevice(VoiceDevice);
		});

		VoiceDevice.on("unregistered", () => {
			console.debug("[CALL] VoiceDevice unregistered");
			store.commit("voice/setReady", false);
		});

		VoiceDevice.on("incoming", onInboundCall);

		VoiceDevice.on("error", (err) => {
			console.error("[CALL] VoiceDevice error :", err);
		});

		VoiceDevice.register();

		console.debug("[CALL] VoiceDevice initialized successfully");
	} catch (err) {
		console.error("[CALL] Error initializing VoiceDevice:", err);
		// retry indefinitely when initialization fails
		setTimeout(initVoiceDevice, 3000);
	} finally {
		isInitializing = false;
	}
}

const cleanup = (commit) => {
	console.debug("[CALL] cleanup");
	if (commit) {
		commit("reset");
	} else {
		store.commit("voice/reset");
	}

	refreshAttempts = 0;

	// Disconnect the AudioDeviceManager from the Twilio Device
	const audioDeviceManager = getAudioDeviceManager();

	audioDeviceManager.clearTwilioDevice();

	if (VoiceDevice) {
		VoiceDevice.removeAllListeners("tokenWillExpire");
		VoiceDevice.removeAllListeners("registered");
		VoiceDevice.removeAllListeners("unregistered");
		VoiceDevice.removeAllListeners("incoming");
		VoiceDevice.removeAllListeners("error");

		VoiceDevice.destroy();
		VoiceDevice = null;
	}
};

const useVoice = {
	bootstrap,
	outboundCall,
	pickQueuedCall,
	cleanup,
	answerCall,
	rejectCall
};

export default useVoice;
